import { isPlatformBrowser } from '@angular/common';
import { effect, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { injectMutation, injectQueryClient } from '@tanstack/angular-query-experimental';

import { setUserLanguageEndpointMutation } from 'client/@tanstack/angular-query-experimental.gen';

import { PtAuthSessionService } from '../auth';

const LANGUAGE_STORAGE_KEY = 'pt-language';

/**
 * Maps backend language codes (e.g., "SL", "EN") to Transloco locale codes (e.g., "sl-SI", "en-US").
 */
const LANGUAGE_CODE_MAP: Record<string, string> = {
  SL: 'sl-SI',
  EN: 'en-US',
  'sl-SI': 'sl-SI', // Allow direct locale codes
  'en-US': 'en-US', // Allow direct locale codes
};

/**
 * Maps Transloco locale codes back to backend language codes.
 */
const LOCALE_TO_BACKEND_MAP: Record<string, string> = {
  'sl-SI': 'SL',
  'en-US': 'EN',
};

/**
 * Service for managing language persistence across localStorage and backend.
 *
 * For authenticated users, language is synced with the backend.
 * For all users, language is cached in localStorage for fast initial load.
 */
@Injectable({ providedIn: 'root' })
export class PtLanguageService {
  private readonly transloco = inject(TranslocoService);
  private readonly authSession = inject(PtAuthSessionService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly queryClient = injectQueryClient();

  private isInitialized = false;

  private readonly setLanguageMutation = injectMutation(() =>
    setUserLanguageEndpointMutation({
      throwOnError: false,
    })
  );

  constructor() {
    // Sync language when user authenticates or user data changes
    effect(() => {
      const user = this.authSession.user();
      if (user?.languageId && this.isInitialized) {
        this.syncWithBackend(user.languageId);
      }
    });
  }

  /**
   * Initializes the language from localStorage and syncs with backend if authenticated.
   * Should be called during app initialization.
   */
  initialize(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Load from localStorage first for fast initial render
    const storedLanguage = this.getStoredLanguage();
    if (storedLanguage) {
      this.transloco.setActiveLang(storedLanguage);
    }

    this.isInitialized = true;

    // If user is already authenticated, sync with backend
    const user = this.authSession.user();
    if (user?.languageId) {
      this.syncWithBackend(user.languageId);
    }
  }

  /**
   * Sets the active language and persists it to localStorage and backend (if authenticated).
   * @param localeCode - Transloco locale code (e.g., "sl-SI", "en-US")
   */
  async setLanguage(localeCode: string): Promise<void> {
    // Update TranslocoService immediately
    this.transloco.setActiveLang(localeCode);

    // Save to localStorage immediately
    this.saveToLocalStorage(localeCode);

    // Invalidate all queries to refetch data with new language
    await this.queryClient.invalidateQueries();

    // Save to backend if authenticated
    if (this.authSession.isAuthenticated()) {
      try {
        // Convert locale code to backend format
        const backendCode = this.toBackendCode(localeCode);

        await this.setLanguageMutation.mutateAsync({
          body: { languageId: backendCode },
        });
      } catch (error) {
        // Log error but don't throw - localStorage is already updated
        console.error('Failed to save language to backend:', error);
      }
    }
  }

  /**
   * Syncs the current language with the backend value.
   * Only updates if the backend value differs from the current language.
   */
  private syncWithBackend(backendLanguageId: string): void {
    const localeCode = this.toLocaleCode(backendLanguageId);
    const currentLanguage = this.transloco.getActiveLang();

    if (localeCode !== currentLanguage) {
      this.transloco.setActiveLang(localeCode);
      this.saveToLocalStorage(localeCode);
      // Invalidate all queries to refetch data with new language
      void this.queryClient.invalidateQueries();
    }
  }

  /**
   * Retrieves the stored language from localStorage.
   */
  private getStoredLanguage(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      return localStorage.getItem(LANGUAGE_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Saves the language to localStorage.
   */
  private saveToLocalStorage(languageId: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, languageId);
    } catch (error) {
      console.error('Failed to save language to localStorage:', error);
    }
  }

  /**
   * Converts a backend language code to a Transloco locale code.
   * Examples: "SL" → "sl-SI", "EN" → "en-US"
   */
  private toLocaleCode(backendCode: string): string {
    return LANGUAGE_CODE_MAP[backendCode] ?? backendCode;
  }

  /**
   * Converts a Transloco locale code to a backend language code.
   * Examples: "sl-SI" → "SL", "en-US" → "EN"
   */
  private toBackendCode(localeCode: string): string {
    return LOCALE_TO_BACKEND_MAP[localeCode] ?? localeCode;
  }
}
